/* StripePaymentSheet — custom ORIO-branded payment surface.
   Uses Stripe.js Payment Element (embedded), no redirect to Stripe-hosted page.
   Theme = ORIO warm. Caller passes client_secret + onSuccess.
*/
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Sparkles, Loader2, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { getStripePromise, ORIO_STRIPE_APPEARANCE } from "@/lib/stripe";
import { hapticSuccess } from "@/lib/haptics";

export default function StripePaymentSheet({
  open,
  onClose,
  clientSecret,
  publishableKey,
  paymentIntentId,
  pack,
  onSuccess,
}) {
  const stripePromise = useMemo(() => getStripePromise(publishableKey), [publishableKey]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1500] flex items-end sm:items-center justify-center pb-[160px] sm:pb-0"
        data-testid="stripe-payment-sheet"
      >
        <div className="absolute inset-0 bg-black/35 backdrop-blur-md" onClick={onClose} />
        <motion.div
          initial={{ y: 80, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 280 }}
          className="relative w-full sm:max-w-md mx-3 sm:mx-0 rounded-t-[28px] sm:rounded-[28px] bg-white border border-[#F0EBE3] overflow-hidden"
          style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.30)", maxHeight: "92dvh" }}
        >
          {/* Header */}
          <div className="relative px-5 pt-5 pb-3 border-b border-[#F0EBE3]">
            <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full opacity-50"
              style={{ background: "radial-gradient(circle, rgba(255,122,26,0.30) 0%, transparent 60%)" }} />
            <div className="absolute -top-8 -left-12 h-28 w-28 rounded-full opacity-40"
              style={{ background: "radial-gradient(circle, rgba(45,186,96,0.30) 0%, transparent 60%)" }} />
            <div className="relative flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#FF7A1A] inline-flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" /> Pagamento sicuro
                </div>
                <h2 className="mt-1 font-display text-2xl text-[#1A1A1A] leading-tight">
                  {pack?.label || "Acquisto"}
                </h2>
                <div className="mt-1 text-[13px] text-[#6B6B6B]">
                  Totale <span className="font-display text-[18px] text-[#1A1A1A]">€ {Number(pack?.eur || 0).toFixed(2)}</span>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="stripe-sheet-close"
                aria-label="Chiudi"
                className="h-9 w-9 rounded-full bg-[#F4EFE6] hover:bg-[#EAE3D2] flex items-center justify-center"
              >
                <X className="h-4 w-4 text-[#6B6B6B]" />
              </button>
            </div>
          </div>

          {/* Stripe Elements */}
          {stripePromise && clientSecret ? (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: ORIO_STRIPE_APPEARANCE, locale: "it" }}>
              <PaymentForm
                paymentIntentId={paymentIntentId}
                pack={pack}
                onSuccess={(res) => { hapticSuccess(); onSuccess?.(res); }}
                onClose={onClose}
              />
            </Elements>
          ) : (
            <div className="px-5 py-10 text-center text-[#6B6B6B] text-sm inline-flex items-center justify-center gap-2 w-full">
              <Loader2 className="h-4 w-4 animate-spin" /> Preparazione del modulo di pagamento…
            </div>
          )}

          {/* Footer reassurance */}
          <div className="px-5 py-3 border-t border-[#F0EBE3] text-[11px] text-[#9A9A9A] bg-[#FBF7F1]">
            <div className="inline-flex items-center gap-2 w-full">
              <ShieldCheck className="h-3.5 w-3.5 text-[#2DBA60] shrink-0" />
              <span>Pagamento protetto da Stripe. ORIO non vede né salva i dati della tua carta.</span>
            </div>
            <div className="mt-2 pt-2 border-t border-[#F0EBE3]/70 text-center text-[10px] leading-snug text-[#9A9A9A]">
              <strong className="text-[#6B6B6B]">ORIO</strong> è un servizio di{" "}
              <a href="https://vixelgraphic.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#FF7A1A] hover:underline">
                Vixel
              </a>{" "}— P.IVA <span className="tabular-nums text-[#6B6B6B]">18531031005</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function PaymentForm({ paymentIntentId, pack, onSuccess, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (stripeError) {
        setError(stripeError.message || "Errore nel pagamento");
        toast.error(stripeError.message || "Pagamento rifiutato");
        setSubmitting(false);
        return;
      }
      // Tell our backend to credit the balance (idempotent)
      const { data } = await api.post("/payments/confirm", { payment_intent_id: paymentIntentId });
      if (data?.ok) {
        toast.success(`+${data.credited_qty || pack?.qty} Super Like aggiunti!`);
        onSuccess?.(data);
      } else {
        setError("Pagamento ricevuto ma in attesa di conferma…");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="px-5 py-5 overflow-y-auto" style={{ maxHeight: "55dvh" }}>
      <PaymentElement options={{ layout: "tabs" }} />
      {error && (
        <div className="mt-3 px-3 py-2 rounded-xl bg-[#FBE3E8] text-[#E94545] text-[12px]" data-testid="stripe-error">
          {error}
        </div>
      )}
      <motion.button
        whileTap={{ scale: 0.97 }}
        type="submit"
        disabled={!stripe || submitting}
        data-testid="stripe-pay-btn"
        className="mt-5 w-full py-3.5 rounded-full text-white font-semibold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
        style={{
          background: "linear-gradient(135deg, #FF5C2A 0%, #FF7A1A 50%, #F4B83C 100%)",
          boxShadow: "0 14px 30px -10px rgba(255,122,26,0.55)",
        }}
      >
        {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Elaborazione…</> : <>Paga € {Number(pack?.eur || 0).toFixed(2)}</>}
      </motion.button>
      <button
        type="button"
        onClick={onClose}
        className="mt-2 w-full py-2 text-[12px] text-[#9A9A9A] hover:text-[#1A1A1A]"
      >
        Annulla
      </button>
    </form>
  );
}
